import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { bossDisplayName } from '../../../shared/domain/bossPresentation.js';
import { formatRaidHpEok } from '../../../shared/domain/raidHpFormat.js';
import { DororaidIcon } from '../../../shared/ui/DororaidIcon.jsx';
import { RaidPracticeDeckPicker } from '../../raid-practice/shared/RaidPracticeDeckPicker.jsx';
import { RaidPracticeSquadDetailModal } from '../../raid-practice/shared/RaidPracticeSquadDetailModal.jsx';
import { RaidTetrisMiniDeck } from './RaidTetrisMiniDeck.jsx';

function textValue(value) {
  return String(value ?? '').trim();
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function memberOpenid(value = {}) {
  return textValue(value.openid ?? value.memberOpenid ?? value.member_openid);
}

function formatEok(value) {
  return `${numberValue(value).toLocaleString('ko-KR', { maximumFractionDigits: 3 })}억`;
}

// 부족/초과 are HP quantities, so they follow the site-wide HP truncation, not damage formatting.
function diffText(value) {
  const amount = numberValue(value);
  return amount >= 0 ? `초과 +${formatRaidHpEok(amount)}` : `부족 ${formatRaidHpEok(Math.abs(amount))}`;
}

function isManualItem(item = {}) {
  return textValue(item.practiceId).startsWith('manual:');
}

function manualId() {
  const random = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `manual:${random}`;
}

const SORT_STORAGE_KEY = 'dororaid:strategy-slot-sort';
const SORT_MODES = new Set(['default', 'damage', 'sync']);
// Leaders usually rule the same few members out of a boss for the whole session (away, saving decks),
// so the choice sticks between openings instead of being retyped into the search box every time.
const EXCLUDED_STORAGE_KEY = 'dororaid:strategy-slot-excluded-members';

function readStoredExcluded() {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(EXCLUDED_STORAGE_KEY) || '[]');
    return new Set(Array.isArray(parsed) ? parsed.map((value) => String(value || '').trim()).filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function storeExcluded(openids) {
  try {
    window.localStorage.setItem(EXCLUDED_STORAGE_KEY, JSON.stringify([...openids]));
  } catch {
    // private windows and blocked site data are fine; the choice just will not persist.
  }
}

function readStoredSortMode() {
  try {
    const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
    return SORT_MODES.has(stored) ? stored : 'default';
  } catch {
    return 'default';
  }
}

function storeSortMode(mode) {
  try {
    window.localStorage.setItem(SORT_STORAGE_KEY, mode);
  } catch {
    // private windows and blocked site data are fine; the choice just will not persist.
  }
}

function candidateToItem(candidate, note = '') {
  return {
    practiceId: textValue(candidate?.id),
    memberOpenid: textValue(candidate?.memberOpenid),
    nickname: textValue(candidate?.nickname) || '닉네임 확인 불가',
    bossNo: numberValue(candidate?.bossNo),
    slotNo: numberValue(candidate?.slotNo, 1),
    title: textValue(candidate?.title) || `모의전 ${numberValue(candidate?.slotNo, 1)}`,
    damageEok: numberValue(candidate?.damageEok),
    deck: Array.isArray(candidate?.deck) ? candidate.deck : [],
    note: textValue(note),
  };
}

export function RaidStrategySlotDialog({
  open = false,
  boss = null,
  phaseNo = 1,
  rowIndex = 0,
  currentItem = null,
  candidates = [],
  members = [],
  recommendedIds = new Set(),
  assignedLocationById = new Map(),
  candidateReasonById = new Map(),
  candidateComparableIds = new Set(),
  syncLevelByOpenid = new Map(),
  usedNikkeCodesByMember = new Map(),
  canManage = false,
  mobile = false,
  onApply = () => '',
  onComparePlacement = null,
  onPreviewSlotDamage = null,
  onDelete = () => {},
  onClose = () => {},
}) {
  const [mode, setMode] = useState('practice');
  const [query, setQuery] = useState('');
  const [selectedPractice, setSelectedPractice] = useState(null);
  const [excludedOpenids, setExcludedOpenids] = useState(readStoredExcluded);
  const [memberFilterOpen, setMemberFilterOpen] = useState(false);
  const [manualMemberOpenid, setManualMemberOpenid] = useState('');
  const [manualDeck, setManualDeck] = useState(Array(5).fill(''));
  const [manualDamage, setManualDamage] = useState('');
  const [manualNote, setManualNote] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [detailTarget, setDetailTarget] = useState(null);
  const [sortMode, setSortMode] = useState(() => readStoredSortMode());

  const sortedMembers = useMemo(() => [...members]
    .filter((member) => memberOpenid(member))
    .sort((left, right) => textValue(left.nickname).localeCompare(textValue(right.nickname), 'ko-KR')), [members]);
  const memberNameByOpenid = useMemo(() => new Map(sortedMembers.map((member) => [
    memberOpenid(member),
    textValue(member.nickname) || '닉네임 확인 불가',
  ])), [sortedMembers]);
  const manualExcludedCodes = useMemo(() => [
    ...(usedNikkeCodesByMember.get(manualMemberOpenid) || []),
  ], [manualMemberOpenid, usedNikkeCodesByMember]);

  // Reset when the dialog opens on a different slot — not whenever the board hands over new
  // `candidates`/`currentItem` object identities. Those change on every silent raid refresh (and a
  // boss with no candidates gets a fresh `[]` on each render), which used to wipe what the leader
  // had selected or typed here mid-edit.
  const latestRef = useRef({ candidates, currentItem });
  latestRef.current = { candidates, currentItem };
  useEffect(() => {
    if (!open) return;
    const item = latestRef.current.currentItem || {};
    const manual = isManualItem(item);
    const matchedPractice = manual
      ? null
      : latestRef.current.candidates.find((candidate) => candidate.id === item.practiceId) || null;
    setMode(manual ? 'manual' : 'practice');
    setQuery('');
    setErrorMessage('');
    setSelectedPractice(matchedPractice);
    setManualMemberOpenid(manual ? textValue(item.memberOpenid) : '');
    setManualDeck(manual && Array.isArray(item.deck) ? [...item.deck] : Array(5).fill(''));
    setManualDamage(manual ? String(numberValue(item.damageEok, '')) : '');
    setManualNote(manual ? textValue(item.note) : '');
  }, [boss?.bossNo, currentItem?.practiceId, open, phaseNo, rowIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, open]);

  const candidateDisabledReason = (candidate) => {
    const assigned = assignedLocationById.get(candidate.id);
    if (assigned && candidate.id !== currentItem?.practiceId) return `이미 ${assigned}에 배치됨`;
    return textValue(candidateReasonById.get(candidate.id));
  };

  // Blocked because the member already spends one of these Nikke on another attack: that is a
  // placement trade, not a dead end, so the card stays clickable and offers the comparison.
  const candidateComparable = (candidate) => Boolean(onComparePlacement)
    && candidateComparableIds.has(candidate.id)
    && textValue(candidate.memberOpenid) === textValue(currentItem?.memberOpenid);

  // Grouped, not just sorted: a slot the leader cannot pick (already placed elsewhere, 3타 초과,
  // 니케 중복) used to sit between usable ones, so the list had to be read rather than scanned.
  const candidateGroups = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase('ko-KR');
    const syncLevel = (candidate) => {
      const value = Number(syncLevelByOpenid.get(textValue(candidate.memberOpenid)));
      return Number.isFinite(value) && value > 0 ? value : -1;
    };
    const compare = (left, right) => {
      if (sortMode === 'damage') return numberValue(right.damageEok) - numberValue(left.damageEok);
      if (sortMode === 'sync') return syncLevel(right) - syncLevel(left) || numberValue(right.damageEok) - numberValue(left.damageEok);
      return Number(recommendedIds.has(right.id)) - Number(recommendedIds.has(left.id))
        || textValue(left.nickname).localeCompare(textValue(right.nickname), 'ko-KR')
        || numberValue(left.slotNo) - numberValue(right.slotNo);
    };

    const usable = [];
    const comparable = [];
    const blocked = [];
    for (const candidate of candidates) {
      if (excludedOpenids.has(textValue(candidate.memberOpenid))) continue;
      if (needle && !`${candidate.nickname} ${candidate.title} ${candidate.slotNo}`.toLocaleLowerCase('ko-KR').includes(needle)) continue;
      const reason = candidateDisabledReason(candidate);
      if (!reason) usable.push(candidate);
      else if (candidateComparable(candidate)) comparable.push(candidate);
      else blocked.push(candidate);
    }
    return {
      usable: usable.sort(compare),
      comparable: comparable.sort(compare),
      blocked: blocked.sort(compare),
      total: usable.length + comparable.length + blocked.length,
    };
  }, [candidateComparableIds, candidateReasonById, candidates, currentItem, excludedOpenids, query, recommendedIds, sortMode, syncLevelByOpenid]);

  // Every member this boss has a registered deck for, so the list to rule out matches what is on screen.
  const memberOptions = useMemo(() => {
    const byOpenid = new Map();
    for (const candidate of candidates) {
      const openid = textValue(candidate.memberOpenid);
      if (!openid || byOpenid.has(openid)) continue;
      byOpenid.set(openid, { memberOpenid: openid, nickname: textValue(candidate.nickname) || '닉네임 확인 불가' });
    }
    return [...byOpenid.values()].sort((left, right) => left.nickname.localeCompare(right.nickname, 'ko-KR'));
  }, [candidates]);

  const toggleExcluded = (openid) => {
    setExcludedOpenids((current) => {
      const next = new Set(current);
      if (next.has(openid)) next.delete(openid);
      else next.add(openid);
      storeExcluded(next);
      return next;
    });
  };

  const clearExcluded = () => {
    setExcludedOpenids(() => {
      storeExcluded([]);
      return new Set();
    });
  };

  const togglePracticeSelection = (candidate) => {
    const selected = selectedPractice?.id === candidate.id;
    setSelectedPractice(selected ? null : candidate);
    setErrorMessage('');
  };

  const applyResult = (item) => {
    const message = textValue(onApply(item));
    if (message) {
      setErrorMessage(message);
      return;
    }
    window.setTimeout(() => {
      if (document.querySelector('.raid-strategy-slot-dialog')) {
        const parentError = textValue(document.querySelector('.raid-strategy-savebar p.error')?.textContent);
        if (parentError) setErrorMessage(parentError);
      }
    }, 0);
  };

  const applyPractice = () => {
    if (!selectedPractice) {
      setErrorMessage('모의전 슬롯을 선택해주세요.');
      return;
    }
    const reason = candidateDisabledReason(selectedPractice);
    if (reason) {
      setErrorMessage(reason);
      return;
    }
    applyResult(candidateToItem(selectedPractice, textValue(currentItem?.note)));
  };

  const applyManual = () => {
    const nickname = memberNameByOpenid.get(manualMemberOpenid) || '';
    const deck = (Array.isArray(manualDeck) ? manualDeck : []).map(textValue).filter(Boolean);
    const damage = numberValue(manualDamage, -1);
    if (!manualMemberOpenid || !nickname) {
      setErrorMessage('멤버를 선택해주세요.');
      return;
    }
    if (deck.length !== 5 || new Set(deck).size !== 5) {
      setErrorMessage('중복 없이 니케 5명을 선택해주세요.');
      return;
    }
    if (damage <= 0) {
      setErrorMessage('딜량을 0보다 크게 입력해주세요.');
      return;
    }
    applyResult({
      practiceId: isManualItem(currentItem || {}) ? currentItem.practiceId : manualId(),
      memberOpenid: manualMemberOpenid,
      nickname,
      bossNo: numberValue(boss?.bossNo),
      slotNo: 1,
      title: '직접 입력',
      damageEok: damage,
      deck,
      note: textValue(manualNote),
    });
  };

  const previewDamage = mode === 'practice'
    ? numberValue(selectedPractice?.damageEok, -1)
    : numberValue(manualDamage, -1);
  const swapPreview = onPreviewSlotDamage && previewDamage >= 0 ? onPreviewSlotDamage(previewDamage) : null;
  const swapChanged = Boolean(swapPreview) && Math.abs(swapPreview.nextDamageEok - swapPreview.currentDamageEok) > 0.000001;

  if (!open || !boss) return null;

  const selectedDeckCount = manualDeck.map(textValue).filter(Boolean).length;
  const manualReady = Boolean(manualMemberOpenid && selectedDeckCount === 5 && numberValue(manualDamage, 0) > 0);
  const practiceReady = Boolean(selectedPractice && !candidateDisabledReason(selectedPractice));
  const blockedSelectionReason = selectedPractice && candidateComparable(selectedPractice)
    ? `${candidateDisabledReason(selectedPractice)} 이 덱을 여기에 쓰려면 ${selectedPractice.nickname}의 다른 슬롯 덱을 바꿔야 합니다.`
    : '';
  const applyReady = mode === 'practice' ? practiceReady : manualReady;
  const applyCurrent = mode === 'practice' ? applyPractice : applyManual;

  const dialog = (
    <div className={`raid-strategy-slot-overlay entry-v2 ${mobile ? 'mobile' : 'desktop'}`} role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="raid-strategy-slot-dialog entry-v2" role="dialog" aria-modal="true" aria-label={`${bossDisplayName(boss)} ${phaseNo}단 슬롯 ${rowIndex + 1} 설정`}>
        <header className="raid-strategy-slot-dialog-head">
          <div className="raid-strategy-slot-dialog-title">
            <span>RAID PLAN ENTRY</span>
            <strong>{bossDisplayName(boss)} 계획 입력</strong>
            <small>{phaseNo}단 · 슬롯 {rowIndex + 1} · 등록 모의전 선택 또는 직접 입력</small>
          </div>
          <div className="raid-strategy-slot-head-actions">
            {onComparePlacement && currentItem?.memberOpenid ? <button type="button" className="raid-strategy-compare-placement" onClick={() => onComparePlacement(currentItem.memberOpenid, '')}>니케 배치 비교</button> : null}
            <button type="button" className="save" disabled={!canManage || !applyReady} onClick={applyCurrent}>적용</button>
            <button type="button" className="ui-icon-action close" onClick={onClose} aria-label="닫기" title="닫기"><DororaidIcon name="close" size={20} /></button>
          </div>
        </header>

        <div className="raid-strategy-slot-mode" role="tablist" aria-label="슬롯 입력 방식">
          <button type="button" role="tab" className={mode === 'practice' ? 'active' : ''} aria-selected={mode === 'practice'} onClick={() => { setMode('practice'); setErrorMessage(''); }}><strong>모의전 선택</strong><small>저장된 슬롯 사용</small></button>
          <button type="button" role="tab" className={mode === 'manual' ? 'active' : ''} aria-selected={mode === 'manual'} onClick={() => { setMode('manual'); setErrorMessage(''); }}><strong>직접 입력</strong><small>멤버·덱 직접 작성</small></button>
        </div>

        {mode === 'practice' ? (
          <div className="raid-strategy-practice-select">
            <div className="raid-strategy-slot-search-row">
              <label className="raid-strategy-slot-search"><span>멤버·슬롯 검색</span><input type="search" value={query} onChange={(event) => setQuery(event.target.value)} /></label>
              <label className="raid-strategy-slot-sort"><span>정렬</span><select value={sortMode} onChange={(event) => { setSortMode(event.target.value); storeSortMode(event.target.value); }}><option value="default">기본</option><option value="damage">딜량순</option><option value="sync">싱크로 레벨순</option></select></label>
              <button type="button" className={`raid-strategy-slot-member-filter-open ${excludedOpenids.size ? 'active' : ''}`.trim()} aria-expanded={memberFilterOpen} onClick={() => setMemberFilterOpen((current) => !current)}>
                멤버 제외{excludedOpenids.size ? ` ${excludedOpenids.size}` : ''}
              </button>
              <button type="button" className="raid-strategy-detail-button" disabled={!selectedPractice} onClick={() => setDetailTarget({ deck: selectedPractice?.deck || [], memberOpenid: selectedPractice?.memberOpenid || '', nickname: selectedPractice?.nickname || '' })}><DororaidIcon name="details" size={16} />상세정보</button>
            </div>
            {memberFilterOpen ? (
              <div className="raid-strategy-slot-member-filter">
                <div className="raid-strategy-slot-member-filter-head">
                  <span>목록에서 뺄 멤버를 고르세요</span>
                  <button type="button" disabled={!excludedOpenids.size} onClick={clearExcluded}>모두 해제</button>
                </div>
                <div className="raid-strategy-slot-member-chips">
                  {memberOptions.map((option) => {
                    const excluded = excludedOpenids.has(option.memberOpenid);
                    return (
                      <button type="button" key={option.memberOpenid} className={excluded ? 'excluded' : ''} aria-pressed={excluded} onClick={() => toggleExcluded(option.memberOpenid)}>
                        {option.nickname}
                      </button>
                    );
                  })}
                  {!memberOptions.length ? <small>이 보스에 등록된 모의전이 없습니다.</small> : null}
                </div>
              </div>
            ) : null}
            <div className="raid-strategy-practice-options">
              {[
                { key: 'usable', list: candidateGroups.usable, label: '' },
                { key: 'comparable', list: candidateGroups.comparable, label: '니케가 겹치는 후보 · 배치 비교로 확인' },
                { key: 'blocked', list: candidateGroups.blocked, label: '지금은 쓸 수 없는 후보' },
              ].flatMap((group) => (group.list.length ? [
                group.label ? <p className="raid-strategy-practice-group" key={`group-${group.key}`}>{group.label} ({group.list.length})</p> : null,
                ...group.list.map((candidate) => {
                  const reason = candidateDisabledReason(candidate);
                  const comparable = Boolean(reason) && candidateComparable(candidate);
                  const selected = selectedPractice?.id === candidate.id;
                  const syncLevel = Number(syncLevelByOpenid.get(textValue(candidate.memberOpenid)));
                  const syncText = Number.isFinite(syncLevel) && syncLevel > 0 ? ` · 싱크로 ${syncLevel}` : '';

                  return (
                    <button type="button" className={`${selected ? 'selected' : ''} ${recommendedIds.has(candidate.id) ? 'recommended' : ''} ${comparable ? 'comparable' : ''}`.trim()} disabled={!canManage || (Boolean(reason) && !comparable)} key={candidate.id} aria-pressed={selected} title={`${candidate.nickname} · ${candidate.title || `모의전 ${candidate.slotNo}`} · 슬롯 ${candidate.slotNo}${syncText}${reason ? ` · ${reason}` : ''}`} onClick={() => togglePracticeSelection(candidate)}>
                      <div className="raid-strategy-practice-meta"><strong>{candidate.nickname}</strong><span>{candidate.title || `모의전 ${candidate.slotNo}`} · 슬롯 {candidate.slotNo}</span><small>{reason ? `${reason}${comparable ? ' · 배치 비교 가능' : ''}` : `${syncText && sortMode === 'sync' ? `싱크로 ${syncLevel} · ` : ''}${recommendedIds.has(candidate.id) ? '현재 단수 추천 후보' : '선택 가능'}`}</small></div>
                      <RaidTetrisMiniDeck deck={candidate.deck || []} compact />
                      <b>{formatEok(candidate.damageEok)}</b>
                    </button>
                  );
                }),
              ] : [])).filter(Boolean)}
              {!candidateGroups.total ? (
                <p>{excludedOpenids.size ? '제외한 멤버를 빼고 나면 남는 후보가 없습니다.' : '이 보스에 등록된 모의전 슬롯이 없습니다.'}</p>
              ) : null}
            </div>
          </div>
        ) : (
          <div className="raid-strategy-manual-form">
            <div className="raid-strategy-manual-primary">
              <label><span>멤버</span><select disabled={!canManage} value={manualMemberOpenid} onChange={(event) => { const nextMember = event.target.value; setManualMemberOpenid(nextMember); setManualDeck(Array(5).fill('')); setErrorMessage(''); }}><option value="">멤버 선택</option>{sortedMembers.map((member) => <option value={memberOpenid(member)} key={memberOpenid(member)}>{textValue(member.nickname) || '닉네임 확인 불가'}</option>)}</select></label>
              <label><span>예상 딜량</span><div className="raid-strategy-damage-input"><input type="number" min="0" step="0.001" disabled={!canManage} value={manualDamage} onChange={(event) => setManualDamage(event.target.value)} /><b>억</b></div></label>
            </div>

            <section className={`raid-strategy-manual-deck ${manualMemberOpenid ? 'ready' : 'locked'}`}>
              <div className="raid-strategy-manual-section-head">
                <div><span>SQUAD SET</span><small>{manualMemberOpenid ? (manualExcludedCodes.length ? `이미 사용한 니케 ${manualExcludedCodes.length}명 제외` : '선택한 멤버의 보유 니케 기준') : '멤버를 먼저 선택해야 합니다.'}</small></div>
                <button type="button" className="raid-strategy-detail-button" disabled={!manualMemberOpenid} onClick={() => setDetailTarget({ deck: manualDeck, memberOpenid: manualMemberOpenid, nickname: memberNameByOpenid.get(manualMemberOpenid) || '' })}><DororaidIcon name="details" size={14} />상세정보</button>
                <b>{selectedDeckCount}/5</b>
              </div>
              {manualMemberOpenid ? (
                <RaidPracticeDeckPicker value={manualDeck} onChange={setManualDeck} memberOpenid={manualMemberOpenid} excludedCodes={manualExcludedCodes} mobile={mobile} />
              ) : (
                <div className="raid-strategy-manual-deck-placeholder"><strong>멤버를 먼저 선택하세요</strong><span>멤버 선택 후 해당 멤버의 니케 목록을 불러옵니다.</span></div>
              )}
            </section>

            <label className="raid-strategy-manual-note"><span>공격 지시</span><textarea rows="2" maxLength={500} disabled={!canManage} value={manualNote} onChange={(event) => setManualNote(event.target.value)} /></label>
          </div>
        )}

        {swapPreview && swapChanged ? (
          <dl className={`raid-strategy-slot-preview ${swapPreview.afterDiffEok >= 0 ? 'enough' : 'lack'}`}>
            <div><dt>이 슬롯</dt><dd>{swapPreview.hasCurrent ? `${formatEok(swapPreview.currentDamageEok)} → ` : ''}<b>{formatEok(swapPreview.nextDamageEok)}</b></dd></div>
            <div><dt>보스 합계</dt><dd>{formatEok(swapPreview.beforeTotalEok)} → <b>{formatEok(swapPreview.afterTotalEok)}</b></dd></div>
            <div><dt>목표 {formatRaidHpEok(swapPreview.targetHpEok)} 대비</dt><dd>{diffText(swapPreview.beforeDiffEok)} → <b>{diffText(swapPreview.afterDiffEok)}</b></dd></div>
          </dl>
        ) : null}
        {blockedSelectionReason ? (
          <div className="raid-strategy-slot-blocked" role="status">
            <p>{blockedSelectionReason}</p>
            <button type="button" onClick={() => onComparePlacement(selectedPractice.memberOpenid, selectedPractice.id)}>니케 배치 비교</button>
          </div>
        ) : null}
        {errorMessage ? <p className="raid-strategy-slot-error" role="alert">{errorMessage}</p> : null}
        {currentItem && canManage ? <footer className="raid-strategy-slot-dialog-actions"><button type="button" className="danger" onClick={onDelete}>슬롯 비우기</button></footer> : null}
      </section>
      {detailTarget ? <RaidPracticeSquadDetailModal deck={detailTarget.deck} personalEnabled memberOpenid={detailTarget.memberOpenid} onClose={() => setDetailTarget(null)} /> : null}
    </div>
  );

  return createPortal(dialog, document.body);
}
